import { Injectable } from '@angular/core';
import {
    HttpClient,
    HttpErrorResponse,
    HttpHeaders,
} from '@angular/common/http';
import { Observable, catchError, firstValueFrom, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Empresa } from '../interfaces/empresa.interface';
import { KeycloakService } from 'keycloak-angular';
import { RegistroService } from 'src/app/registro.service';
import { HistoricoRepressentanteTranspaso } from '../interfaces/historico_representante.interface copy';

@Injectable({
    providedIn: 'root',
})
export class EmpresaService {
    perfilUsuario;
    private apiUrl: string = environment.apiSiriUrl;
    constructor(
        private http: HttpClient,
        protected readonly keycloakService: KeycloakService,
        private registroService: RegistroService
    ) {}

    getEmpresas(): Observable<Empresa[]> {
        const url = `${this.apiUrl}agente-vigilado`;
        return this.http.get<Empresa[]>(url);
    }

    /* Departamentos */
    getDepartamentos(): Observable<any[]> {
        const url = `${this.apiUrl}departamento`;
        return this.http
            .get<any[]>(url)
            .pipe(catchError(this.handleErrorResponse));
    }

    /**
     * Verifica si un NIT ya se encuentra registrado .
     */
    checkNit(nit: string): Observable<any> {
        const url = `${this.apiUrl}agente-vigilado/check-nit`;
        const headers = new HttpHeaders().set('x-nit', nit);
        return this.http.get<any>(url, { headers }).pipe(
            catchError((error) => {
                return throwError(() => error);
            })
        );
    }
    /* cargos */
    getCargos(): Observable<any[]> {
        const url = `${this.apiUrl}agente-vigilado/cargos`;
        return this.http
            .get<any[]>(url)
            .pipe(catchError(this.handleErrorResponse));
    }

    getMunicipios(idDepart: string): Observable<any[]> {
        const url = `${this.apiUrl}municipio/by-departamento/${idDepart}`;
        return this.http
            .get<any[]>(url)
            .pipe(catchError(this.handleErrorResponse));
    }

    /* Traer la empresa por id */
    getEmpresaByNit(nit: string): Observable<Empresa | undefined> {
        const url = `${this.apiUrl}agente-vigilado/nit/${nit}`;
        return this.http.get<Empresa>(url);
    }

    /** Verifica si la empresa requiere actualización de datos */
    validarDatosEmpresa(id: number): Observable<any> {
        const url = `${this.apiUrl}agente-vigilado/${id}/validar-datos`;
        return this.http.get<any>(url).pipe(
            catchError((error) => {
                console.error('Error al validar datos de la empresa:', error);
                return throwError(
                    () => new Error('Error al validar datos de la empresa')
                );
            })
        );
    }

    async crearEmpresaConHistorico(empresaData: any, representanteData: any) {
        try {
            const empresaResponse = await firstValueFrom(
                this.http.post<Empresa>(
                    `${this.apiUrl}agente-vigilado`,
                    empresaData
                )
            );

            if (!empresaResponse || !empresaResponse.id) {
                throw new Error('Respuesta inválida del servidor');
            }
            const representanteAgenteVigiladoStr = localStorage.getItem(
                'representanteAgenteVigilado'
            );
            let representanteAgenteVigiladoId: string | null = null;

            if (representanteAgenteVigiladoStr) {
                try {
                    const representanteAgenteVigilado = JSON.parse(
                        representanteAgenteVigiladoStr
                    );
                    representanteAgenteVigiladoId =
                        representanteAgenteVigilado?.id || null;
                } catch (error) {
                    console.error('Error al parsear JSON:', error);
                }
            }
            return empresaResponse;
        } catch (error) {
            console.error('Error en crearEmpresaConHistorico:', error);
            throw error;
        }
    }
    /**
     *  Crea un comercializador enviando la información de agente vigilado y caracterización del comercializador.
     *
     * @param comercializadorData
     * @returns
     */
    crearComercializador(comercializadorData: any): Observable<any> {
        const url = `${this.apiUrl}agente-vigilado/comercializador`;
        return this.http.post(url, comercializadorData);
    }

    /**
     *  Actualiza un comercializador enviando la información de agente vigilado y caracterización del comercializador.
     *
     * @param comercializadorData
     * @returns
     */
    actualizarComercializador(comercializadorData: any): Observable<any> {
        const url = `${this.apiUrl}agente-vigilado/comercializador`;
        return this.http.put(url, comercializadorData);
    }

    async asociarAgenteVigilado(empresa: any, cargoRepresentante) {
        const textoCorreo =
            'Solicitud de sustitucion representante del agente vigilado.';
        const historicoRepresentante: HistoricoRepressentanteTranspaso = {
            to: empresa.correo_corporativo,
            subject: textoCorreo,
            text: textoCorreo,
            agenteVigiladoId: empresa.id,
            representanteAgenteVigiladoId: this.representanteId(),
            cargoRepresentante: cargoRepresentante,
            relacionActiva: true,
        };
        await firstValueFrom(
            this.http.post(
                `${this.apiUrl}historico-representantes-agente-vigilado/send`,
                historicoRepresentante
            )
        );
    }

    async representanteId() {
        try {
            let representanteData = null;
            while (!representanteData) {
                representanteData = localStorage.getItem(
                    'representanteAgenteVigilado'
                );
                if (representanteData) {
                    representanteData = JSON.parse(representanteData);
                    break;
                }
                await new Promise((resolve) => setTimeout(resolve, 100));
            }
            if (!representanteData.usuarioId) {
                throw new Error(
                    'No se encontró el usuarioId del representante'
                );
            }
            return representanteData.usuarioId;
        } catch (error) {
            throw error;
        }
    }

    /* Obtiene la información de una empresa (agente vigilado) según su identificador. */
    findByEmpresaId(empresaId: number): Observable<any> {
        return this.http.get(`${this.apiUrl}agente-vigilado/${empresaId}`);
    }

    actualizarEmpresa(nit: string, empresa: Empresa): Observable<Empresa> {
        const url = `${this.apiUrl}agente-vigilado/${nit}`;
        return this.http
            .put<Empresa>(url, empresa)
            .pipe(catchError(this.handleErrorResponse));
    }

    crearAgenteComercializador(data: any): Observable<any> {
        const url = `${this.apiUrl}agente-vigilado/comercializador`;
        return this.http.post(url, data);
    }

    /**
     * Verifica si una empresa tiene productos registrados
     * Utiliza diferentes endpoints según el eslabón de la empresa:
     * - Comercializador (solo o con auxiliares): usa endpoint de registros-comercializador
     * - Productor/Importador: usa endpoint de producto/agente-vigilado
     *
     * @param empresaId ID de la empresa a verificar
     * @param eslabon Eslabón actual de la empresa (formato: "Productor/Comercializador")
     * @returns Observable<any> Respuesta con los productos de la empresa
     */
    empresaTieneProductos(empresaId: number, eslabon: string): Observable<any> {
        const eslabones = eslabon ? eslabon.split('/') : [];
        const tieneCom = eslabones.includes('Comercializador');
        const tieneProdOImp =
            eslabones.includes('Productor') || eslabones.includes('Importador');
        const esSoloComercializador = tieneCom && !tieneProdOImp;

        let url: string;

        if (esSoloComercializador) {
            url = `${this.apiUrl}registros-comercializador?agenteVigiladoId=${empresaId}&page=1&limit=1`;
        } else {
            url = `${this.apiUrl}producto/agente-vigilado/${empresaId}`;
        }

        return this.http.get<any>(url).pipe(
            catchError((error) => {
                console.error('Error al verificar productos:', error);
                return throwError(() => error);
            })
        );
    }

    private handleErrorResponse(error: HttpErrorResponse) {
        console.error(error.error.message);
        return throwError(error.error.message);
    }
}
